<script>
    import { onMount, createEventDispatcher } from 'svelte';
    import Button from '#general/Button.svelte';
    import { buttonStates } from '@repo/config/Global';
    import { getFocusableElements, focusOnFirst } from '@repo/config/Utils';


    /**
     * @typedef {Object} Props
     * @property {any} title
     * @property {string} [confirmLabel]
     * @property {string} [cancelLabel]
     * @property {any} [media]
     * @property {any} [message] Rendered as HTML: never put user data in it.
     * @property {string} [text] Plain-text message, escaped on render. Use it
     *     for anything containing user data.
     * @property {boolean} [hasCloseButton]
     * @property {import('svelte').Snippet} [children]
     */
    let {
        title,
        confirmLabel = 'confirm',
        cancelLabel = 'cancel',
        media = null,
        message = null,
        text = null,
        hasCloseButton = false,
        children = null,
    } = $props();

    const dispatch = createEventDispatcher();

    function handleClick(e) {
        if (e.target.classList.contains('floating-modal-holder')) {
            handleCancel(e);
        }
    }

    function handleCancel(e) {
        e.stopImmediatePropagation();
        e.preventDefault();

        dispatch('cancel', e);
    }

    function handleConfirm(e) {
        e.stopImmediatePropagation();
        e.preventDefault();

        dispatch('confirm');
    }
    
    function handleKeyPress(e) {
        if (e.key === 'Escape') {
            handleCancel(e);
        }

        const focusableContent = getFocusableElements('.floating-modal');
        const firstFocusableElement = focusableContent[0];
        const lastFocusableElement = focusableContent[focusableContent.length - 1];

        if (e.key === 'Tab') {
            if (e.shiftKey) { // If Shift + Tab is pressed
                if (document.activeElement === firstFocusableElement) {
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    
                    lastFocusableElement.focus();
                }
            } else { // If Tab is pressed
                if (document.activeElement === lastFocusableElement) {
                    e.stopImmediatePropagation();
                    e.preventDefault();

                    firstFocusableElement.focus();
                }
            }
        }
    }

    onMount(async () => {
        setTimeout(() => {
            focusOnFirst('.floating-modal'); // focus within the modal
        }, 0);
    });

</script>



<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="floating-modal-holder semi-interactive" onclick={handleClick} onkeydown={handleKeyPress}>

    <div class="floating-modal"
        role="dialog" 
        tabindex="0"
        aria-modal="true" 
        aria-labelledby={title ? 'floating_modal_title' : null}
        aria-describedby={message || text ? 'floating_modal_content' : null}
        onkeydown={handleKeyPress}>
        {#if hasCloseButton}
            <i onclick={handleCancel} class="material-icons" aria-hidden="true">close</i>
        {/if}
        {#if title}
            <h2 id="floating_modal_title">{title}</h2>
            <div class="space"></div>
        {/if}
        {#if message}
            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
            <p id="floating_modal_content">{@html message}</p>
            <div class="space"></div>
        {:else if text}
            <p id="floating_modal_content">{text}</p>
            <div class="space"></div>
        {/if}
        {#if media}
            <div class="media">
                <img src={media} alt=""/>
            </div>
        {/if}
        <div class="row">
            {#if children}
                {@render children()}
            {:else}
                <Button onclick={handleCancel} title={cancelLabel} variant={buttonStates.NEUTRAL} />
                <Button onclick={handleConfirm} title={confirmLabel} variant={buttonStates.WAITING} />
            {/if}
        </div>
    </div>
</div>

<style>
    .floating-modal-holder{
        display:flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        top:0;
        left:0;
        width:100vw;
        height:100vh;
        background-color:rgba(0,0,0,0.75);
        z-index: 9999;
    }

    .floating-modal{
        display:flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        box-sizing: border-box;
        width:fit-content;
        min-width:18rem;
        max-width:24rem;
        background-color:#242121;
        border-radius:1rem;
        padding:1rem;
        box-shadow:0 0.5rem 1rem rgba(0,0,0,0.5);
    }

    .floating-modal h2{
        font-size: 0.75rem;
        font-weight:600;
        text-transform: uppercase;
        color:rgb(74, 198, 255);

    }

    .floating-modal p{
        font-size:0.9rem;
        color: #ffffff;
    }

    .row{
        display:flex;
        align-items: center;
        justify-content: center;
        width:100%;
        gap:1rem;
    }

    i {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        cursor: pointer;
    }
</style>